'use client';

import {
  useCursorFollower,
  UseCursorFollowerOptions,
} from '@/hooks/useCursorFollower';
import {
  AnimatePresence,
  motion,
  MotionProps,
  MotionValue,
} from 'framer-motion';
import * as React from 'react';
import { RefObject, useMemo } from 'react';

type CursorFollowerProps = {
  containerRef: RefObject<HTMLElement>;
  children:
    | React.ReactNode
    | ((state: {
        visible: boolean;
        x: MotionValue<number>;
        y: MotionValue<number>;
      }) => React.ReactNode);
  size?: number;
  className?: string;
  options?: Omit<UseCursorFollowerOptions, 'centerBySize'>;
  motionProps?: {
    initial?: MotionProps['initial'];
    animate?: MotionProps['animate'];
    exit?: MotionProps['exit'];
    transition?: MotionProps['transition'];
  };
};

export function CursorFollower({
  containerRef,
  children,
  size,
  className = 'pointer-events-none absolute z-[100]',
  options,
  motionProps,
}: CursorFollowerProps) {
  const { x, y, visible, prefersReduced } = useCursorFollower(containerRef, {
    centerBySize: size,
    ...options,
  });

  const content = useMemo(
    () =>
      typeof children === 'function'
        ? (children as any)({ visible, x, y })
        : children,
    [children, visible]
  );

  return (
    <AnimatePresence>
      {visible && !prefersReduced && (
        <motion.div
          key="cursor-follower"
          className={className}
          style={{ x, y, width: size, height: size }}
          initial={motionProps?.initial ?? { scale: 0.6, opacity: 0 }}
          animate={motionProps?.animate ?? { scale: 1, opacity: 1 }}
          exit={motionProps?.exit ?? { scale: 0.6, opacity: 0 }}
          transition={
            motionProps?.transition ?? { duration: 0.22, ease: 'easeOut' }
          }
        >
          {content}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
